import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi, setAdminToken, getAdminToken, formatApiErrorDetail } from "../lib/adminApi";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);  // null = checking, false = anon, object = logged in
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    if (!getAdminToken()) {
      setAdmin(false);
      return null;
    }
    try {
      const { data } = await adminApi.get("/admin/auth/me");
      setAdmin(data);
      return data;
    } catch (e) {
      setAdminToken(null);
      setAdmin(false);
      return null;
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await adminApi.post("/admin/auth/login", { email, password });
      setAdminToken(data.token);
      setAdmin(data.admin);
      return data.admin;
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    setError("");
    try {
      const { data } = await adminApi.post("/admin/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      setAdmin(data.admin);
      return data.admin;
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(() => {
    setAdminToken(null);
    setAdmin(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, error, login, logout, changePassword, fetchMe }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
